// src/stateManagers/store/authStore.ts
import { create } from 'zustand';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

export interface AuthUser {
  id: string;
  role: string;
  schoolId?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  setAuth: (auth: Partial<AuthState>) => void;
  clearAuth: () => void;
  initAuth: () => void;
}

const extractUserFromToken = (token: string): AuthUser | null => {
  try {
    const decoded = jwtDecode<{ user?: { id: string; role: string; school_id?: string } }>(token);
    const user = decoded.user;
    if (!user) return null;
    return {
      id: user.id,
      role: user.role,
      schoolId: user.school_id,
    };
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
} as const;

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  loading: true,

  setAuth: (auth) => {
    set((state) => ({ ...state, ...auth }));
  },

  clearAuth: () => {
    Cookies.remove('accessToken', COOKIE_OPTIONS);
    Cookies.remove('refreshToken', COOKIE_OPTIONS);
    set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
    });
  },

  initAuth: () => {
    try {
      const accessToken = Cookies.get('accessToken');
      const refreshToken = Cookies.get('refreshToken');

      if (accessToken && refreshToken) {
        const user = extractUserFromToken(accessToken);
        if (user) {
          set({
            isAuthenticated: true,
            user,
            accessToken,
            refreshToken,
            loading: false,
          });
        } else {
          Cookies.remove('accessToken', COOKIE_OPTIONS);
          Cookies.remove('refreshToken', COOKIE_OPTIONS);
          set({ loading: false });
        }
      } else {
        set({ loading: false });
      }
    } catch (error) {
      console.error('authStore init error:', error);
      Cookies.remove('accessToken', COOKIE_OPTIONS);
      Cookies.remove('refreshToken', COOKIE_OPTIONS);
      set({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
      });
    }
  },
}));